import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormErrorsComponent } from '@spartacus/storefront';
import { OccSiteAdapter, Country } from '@spartacus/core';
import { NgSelectComponent } from '@ng-select/ng-select';
import { catchError, tap, takeUntil, switchMap, filter } from 'rxjs/operators';
import { Observable, Subject, of } from 'rxjs';
import { MultiCartFacade } from '@spartacus/cart/base/root';
import { KnBrCartContextService } from 'src/app/store/kn-br-cart-context/kn-br-cart-context.service';
import { KnBrSalesCheckoutService } from 'src/app/services/kn-br-sales-checkout.service';
import { KnBrPhoneHelper } from 'src/app/shared/helpers/kn-br-phone-helpers';


@Component({
  selector: 'kn-br-request-quote-details',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormErrorsComponent, NgSelectComponent],
  templateUrl: './kn-br-request-quote-details.component.html',
  styleUrls: ['./kn-br-request-quote-details.component.scss'],
  providers: [OccSiteAdapter],
})
export class KnBrRequestQuoteDetailsComponent implements OnInit, OnDestroy {
  stateList: any[] = [];
  objectKeys = Object.keys;
  defaultedCountry = '';
  isCountryLoading = false;
  isRegionLoading = false;
  usaCanadaMexicoCountryList: any[];
  plantLocationList: any[] = [];
  isLabels = false;
  cartContext$: Observable<string> = this.knBrCartContextService.get$;
  shippingForm;
  cartId: string = '';
  cartData: any;
  private destroy$ = new Subject<void>();
  labelsPlantName:string = '';
  uploadedFiles: File[] = [];
  fileCount = 0;
  maxFileSize = 10485760; // 10MB in bytes
  maxFiles = 1000;

  constructor(
    private formBuilder: FormBuilder,
    private occSiteAdapter: OccSiteAdapter,
    private cdr: ChangeDetectorRef,
    private multiCartFacade: MultiCartFacade,
    private knBrCartContextService: KnBrCartContextService,
    private knBrCheckoutService: KnBrSalesCheckoutService,
    private knBrPhoneHelper: KnBrPhoneHelper,
  ) {
    this.shippingForm = this.formBuilder.group({
      company: [''],
      street1: [''],
      street2: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      postal: ['', Validators.required],
      country: ['', Validators.required],
      phone: ['', [Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/)]],
      plantLocation: [''],
      comments: [''],
      multipleShipment: [false],
    });
  }

  ngOnInit(): void {
    this.cartContext$
      .pipe(
        takeUntil(this.destroy$),
        filter((cartId) => !!cartId),
        tap((cartId) => {
          this.cartId = cartId;
          this.multiCartFacade.reloadCart(cartId);
        }),
        switchMap((cartId) => this.multiCartFacade.getCart(cartId)),
        filter((cart) => !!cart),
        catchError((error) => {
          console.error('Error loading cart:', error);
          return of(null);
        })
      )
      .subscribe((cart) => {
        if (!cart) return;

        this.cartData = cart;
        this.isLabels = String((cart as any).cartCategoryBrand || '').toLowerCase().includes('labels');
        this.setPlantLocationValidator(this.isLabels);

        if (this.isLabels) {
          this.loadPlantLocations();
        } else {
          this.plantLocationList = [];
        }

        if (cart?.deliveryAddress) {
          this.populateExistingShippingDetails();
        } else {
          // Only load default countries if no existing delivery address
          this.getCountries();
        }

        this.cdr.detectChanges();
      });
  }

  populateExistingShippingDetails() {
    const deliveryCountry = this.cartData?.deliveryAddress?.country?.isocode;
    const deliveryState = this.cartData?.deliveryAddress?.region?.isocode;

    // Load countries for the dropdown
    this.isCountryLoading = true;
    this.occSiteAdapter.loadCountries().pipe(
      takeUntil(this.destroy$)
    ).subscribe(res => {
      this.usaCanadaMexicoCountryList = res.filter(
        eachItem => ['US', 'MX', 'CA'].includes(eachItem.isocode)
      );
      this.isCountryLoading = false;
      this.cdr.detectChanges();
    });

    // Patch all form values including country and state upfront
    this.shippingForm.patchValue({
      submitterEmail: this.cartData?.deliveryAddress?.email,
      firstName: this.cartData?.deliveryAddress?.firstName,
      lastName: this.cartData?.deliveryAddress?.lastName,
      company: this.cartData?.deliveryAddress?.companyName,
      street1: this.cartData?.deliveryAddress?.line1,
      street2: this.cartData?.deliveryAddress?.line2,
      city: this.cartData?.deliveryAddress?.town,
      postal: this.cartData?.deliveryAddress?.postalCode,
      phone: this.cartData?.deliveryAddress?.phone,
      comments: this.cartData?.deliveryAddress?.note,
      plantLocation: this.cartData?.deliveryAddress?.labelsPlantId,
      country: deliveryCountry,
      state: deliveryState,
      multipleShipment: this.cartData?.multipleShipment,
    });

    // Load and set shipping country states
    if (deliveryCountry) {
      this.occSiteAdapter.loadRegions(deliveryCountry).pipe(
        takeUntil(this.destroy$),
        tap((regions) => {
          this.stateList = regions.filter(eachItem => eachItem.isocode.startsWith(deliveryCountry));
        }),
        catchError(() => {
          this.stateList = [];
          return of([]);
        })
      ).subscribe(() => {
        this.cdr.detectChanges();
      });
    }

    this.selectPlantLocation();
    this.formatPhoneNumber()
    this.cdr.detectChanges()
  }

  private loadPlantLocations(): void {
    this.knBrCheckoutService.getLabelsPlantLocations(this.cartId).pipe(
      takeUntil(this.destroy$),
      catchError(() => of({ plants: [] }))
    ).subscribe((response) => {
     this.plantLocationList = response?.plants;
      this.cdr.detectChanges();
    });
  }

  private setPlantLocationValidator(enabled: boolean): void {
    const control = this.shippingForm.controls.plantLocation;
    if (enabled) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
      control.reset('', { emitEvent: false });
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getCountries() {
    this.isCountryLoading = true;
    this.occSiteAdapter.loadCountries().pipe(
      takeUntil(this.destroy$)
    ).subscribe(res => {
      this.usaCanadaMexicoCountryList = res.filter(
        eachItem => ['US', 'MX', 'CA'].includes(eachItem.isocode)
      );
      const currency = localStorage.getItem('spartacus⚿⚿currency');
      const cleanedCurrency = currency?.replace(/['"]+/g, '');
      const countryIso = cleanedCurrency === 'CAD' ? 'CA' : cleanedCurrency === 'USD' ? 'US' : 'MX';
      this.defaultedCountry = countryIso;
      this.countrySelected({ isocode: countryIso } as Country, 'shipping');
      this.isCountryLoading = false;
      this.cdr.detectChanges();
    })
  }

  countrySelected(country: Country, type: string) {
    if (country && country.isocode) {
      this.shippingForm.controls.state.setValue('');
      this.occSiteAdapter.loadRegions(country.isocode).pipe(
        takeUntil(this.destroy$),
        tap(() => (this.isRegionLoading = false))
      ).subscribe(res => {
          if (res) {
            if (country && country.isocode) {
              this.stateList = res.filter(eachItem => eachItem.isocode.startsWith(country.isocode));
            }
          }
        })
      this.cdr.detectChanges();
      } else if (!country) {
        this.shippingForm.controls.state.setValue('');
        this.stateList = [];
      }
  }
  isInvalid(controlName: string): boolean {
    const field: AbstractControl | null = this.shippingForm.get(controlName);
    return !!field && field.invalid && field.touched;
  }

  private setOvernightValidators(enabled: boolean): void {
    const overnightControls = [
      this.shippingForm.controls.overnightCarrier,
      this.shippingForm.controls.overnightAccountNumber,
      this.shippingForm.controls.billingName,
      this.shippingForm.controls.billingAddr1,
      this.shippingForm.controls.billingCity,
      this.shippingForm.controls.billingState,
      this.shippingForm.controls.billingPostal,
      this.shippingForm.controls.billingCountry,
    ];

    overnightControls.forEach((field) => {
      if (enabled) {
        field.addValidators(Validators.required);
      } else {
        field.removeValidators(Validators.required);
      }
      field.updateValueAndValidity({ emitEvent: false });
    });
  }


  formatPhoneNumber() {
    const phoneCtrl = this.shippingForm.get("phone");
    if (phoneCtrl) {
      const rawValue = String(phoneCtrl.value || '');
      // ✓ Use service method to format phone number
      const formatted =  this.knBrPhoneHelper.formatPhoneNumber(rawValue);;
      phoneCtrl.setValue(formatted, { emitEvent: false });
    }
  }

  selectPlantLocation(){
    this.labelsPlantName = this.plantLocationList.find(plant => plant.plantId === this.shippingForm.controls.plantLocation.value)?.plantName || '';
  }

  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    this.addFiles(Array.from(files));
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const files: FileList | null = event.dataTransfer?.files;
    if (files) {
      this.addFiles(Array.from(files));
    }
  }

  private addFiles(files: File[]): void {
    for (const file of files) {
      // Check file size (max 10MB)
      if (file.size > this.maxFileSize) {
        console.warn(`File ${file.name} exceeds 10MB limit`);
        continue;
      }
      // Check total file count
      if (this.uploadedFiles.length >= this.maxFiles) {
        console.warn(`Cannot upload more than ${this.maxFiles} files`);
        break;
      }
      // Avoid duplicate files
      const isDuplicate = this.uploadedFiles.some(f => f.name === file.name && f.size === file.size);
      if (!isDuplicate) {
        this.uploadedFiles.push(file);
      }
    }
    this.fileCount = this.uploadedFiles.length;
    this.cdr.detectChanges();
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    this.fileCount = this.uploadedFiles.length;
    this.cdr.detectChanges();
  }
}
